import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChatReaction,
  chatReactionApiClient,
} from '@src/api/shared/community/chatReactionApiClient';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useReactionContext } from '@src/context/ReactionContext';
import { TOOLTIP_DISPLAY_TIME } from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';
import Tooltip from '@src/features/shared/community/components/chat/Tooltip';
import useIsMobile from '@src/hooks/useIsMobile';
import { useReactionLoginAlertTooltip } from '@src/hooks/useReactionLoginAlertTooltip';
import { isTenantMember } from '@src/utils/checkTenantMemberPermission';
import { Emoji } from 'emoji-picker-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ReactionMenu } from './ReactionMenu';
import ReactionUserBottomMenu from './ReactionUserBottomMenu';
import ReactionUserDropdownMenu from './ReactionUserDropdownMenu';

interface ReactionItemProps {
  chatId?: number;
  reaction?: ChatReaction;
  allReactions?: ChatReaction[];
  blockParentClick?: boolean;
}

const ReactionItem: React.FC<ReactionItemProps> = ({
  chatId,
  reaction,
  allReactions,
  blockParentClick = false,
}) => {
  const touchTimer = useRef<NodeJS.Timeout | null>(null);
  const { code } = useParams<{ code: string }>();
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  const isMobile = useIsMobile();
  const { profile } = useSharedAuth();
  const isUserTenantMember = isTenantMember(profile);
  const { isLoggedIn, isTooltipVisible, showTooltipIfNotLoggedIn } =
    useReactionLoginAlertTooltip(TOOLTIP_DISPLAY_TIME);

  const [showUserBottomMenu, setShowUserBottomMenu] = useState(false);
  const [showTenantMemberTooltip, setShowTenantMemberTooltip] = useState(false);
  const {
    reactions: reactionState,
    updateReactionUsers,
    updateReactionsOptimistically,
  } = useReactionContext();
  const contextReaction = reactionState[chatId || 0]?.find((r) => r.emoji === reaction?.emoji);
  const localReaction = contextReaction || reaction;

  const [dropdownState, setDropdownState] = useState({
    visible: false,
    position: { x: 0, y: 0 },
    active: false,
    placement: 'bottom' as 'top' | 'bottom',
  });

  const menuRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = useCallback(() => {
    if (emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const menuHeight = menuRef.current?.offsetHeight || 200;

      const shouldShowAbove = spaceBelow < menuHeight && spaceAbove > menuHeight;

      setDropdownState((prev) => ({
        ...prev,
        position: {
          x: rect.left + rect.width / 2,
          y: shouldShowAbove ? rect.top - 4 : rect.bottom + 4,
        },
        placement: shouldShowAbove ? 'top' : 'bottom',
      }));
    }
  }, []);

  const handleReactionClick = useCallback(async () => {
    setShowTenantMemberTooltip(false);
    if (isUserTenantMember) {
      setShowTenantMemberTooltip(true);
      updateDropdownPosition();
      setDropdownState((prev) => ({
        ...prev,
        visible: true,
        active: true,
      }));

      setTimeout(() => {
        setShowTenantMemberTooltip(false);
        setDropdownState((prev) => ({ ...prev, visible: false, active: false }));
      }, TOOLTIP_DISPLAY_TIME);
      return;
    }

    if (showTooltipIfNotLoggedIn()) {
      updateDropdownPosition();
      setDropdownState((prev) => ({
        ...prev,
        visible: true,
        active: true,
      }));
      return;
    }

    if (!code || !chatId || !localReaction) {
      return;
    }

    const emoji = localReaction.emoji || '';

    // 楽観更新: 即座にリアクションを更新
    const currentReactions = reactionState[chatId] || allReactions;
    updateReactionsOptimistically(chatId, emoji, currentReactions);

    const res = await chatReactionApiClient.post(code, chatId, {
      emoji: localReaction.emoji || '',
    });

    handleApiResponse(res, (data) => {
      if (data) {
        // ユーザー情報のみを更新
        updateReactionUsers(chatId, emoji, data.users);
      }
    });
  }, [
    isUserTenantMember,
    showTooltipIfNotLoggedIn,
    code,
    chatId,
    localReaction,
    reactionState,
    allReactions,
    updateReactionsOptimistically,
    updateDropdownPosition,
    updateReactionUsers,
  ]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (!dropdownState.active && dropdownState.visible) {
      timeoutId = setTimeout(() => {
        setDropdownState((prev) => ({ ...prev, visible: false }));
      }, 200);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [dropdownState.active, dropdownState.visible]);

  useEffect(() => {
    return () => {
      if (touchTimer.current) {
        clearTimeout(touchTimer.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isLoggedIn || isMobile || isUserTenantMember) {
      return;
    }
    updateDropdownPosition();
    setDropdownState((prev) => ({
      ...prev,
      active: true,
      visible: true,
    }));
  }, [isLoggedIn, isMobile, isUserTenantMember, updateDropdownPosition]);

  const handleMouseLeave = useCallback(() => {
    if (!isLoggedIn || isMobile || isUserTenantMember) {
      return;
    }
    setDropdownState((prev) => ({ ...prev, active: false }));
  }, [isLoggedIn, isMobile, isUserTenantMember]);

  if (localReaction?.count === 0 && !localReaction?.is_reacted) {
    return null;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
    }

    if (isMobile && isUserTenantMember) {
      setShowTenantMemberTooltip(true);
      setTimeout(() => {
        setShowTenantMemberTooltip(false);
      }, TOOLTIP_DISPLAY_TIME);
      return;
    }

    if (isMobile && !isLoggedIn) {
      showTooltipIfNotLoggedIn();
      return;
    }

    touchTimer.current = setTimeout(() => {
      setShowUserBottomMenu(true);
    }, 600);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
    }
  };

  return (
    <div
      className='inline-block shrink-0'
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
      onClick={(e) => blockParentClick && e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        ref={emojiButtonRef}
        type='button'
        className={`relative rounded-[4px] border border-solid bg-gray-200 px-1 pb-[3px] pt-[2px] text-sm !leading-none transition-colors lg:text-base ${
          !isMobile ? 'hover:border-[#888]' : ''
        } ${localReaction?.is_reacted ? 'border-primary' : 'border-transparent'}`}
        onClick={handleReactionClick}
        onMouseEnter={!isMobile ? handleMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleMouseLeave : undefined}
        onFocus={!isMobile ? handleMouseEnter : undefined}
        onBlur={!isMobile ? handleMouseLeave : undefined}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        onTouchCancel={isMobile ? handleTouchEnd : undefined}
        data-testid='reaction-item'
      >
        <span className='flex select-none items-center gap-2'>
          <Emoji
            unified={localReaction?.emoji?.toLowerCase() ?? ''}
            size={18}
          />
          <span className='text-xs text-gray-500'>{localReaction?.count ?? 0}</span>
        </span>
      </button>
      {/* PC版ツールチップの表示ロジックをここから変更 */}
      {!isMobile && dropdownState.visible && (
        <>
          {isLoggedIn ? (
            isUserTenantMember && showTenantMemberTooltip ? (
              <ReactionMenu>
                <div
                  ref={menuRef}
                  className={`
                    absolute z-[60] -translate-x-1/2 rounded-lg
                    shadow-lg transition-opacity duration-200
                    ${dropdownState.placement === 'top' ? '-translate-y-full' : ''}
                    ${dropdownState.active ? 'pointer-events-none opacity-100' : 'pointer-events-none opacity-0'}
                    text-[12px]
                  `}
                  style={{
                    top: dropdownState.position.y,
                    left: dropdownState.position.x,
                  }}
                >
                  <Tooltip
                    label='リアクション権限がありません。管理者にお問い合わせください。'
                    weight='500'
                  />
                </div>
              </ReactionMenu>
            ) : !isUserTenantMember ? (
              <ReactionMenu>
                <div
                  ref={menuRef}
                  className={`
                  absolute z-[60] -translate-x-1/2 rounded-lg shadow-lg
                  transition-opacity duration-200
                  ${dropdownState.active ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}
                  ${dropdownState.placement === 'top' ? '-translate-y-full' : ''}
                `}
                  style={{
                    top: dropdownState.position.y,
                    left: dropdownState.position.x,
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <ReactionUserDropdownMenu
                    users={localReaction?.users}
                    emoji={localReaction?.emoji}
                  />
                </div>
              </ReactionMenu>
            ) : null
          ) : isTooltipVisible ? (
            <ReactionMenu>
              <div
                ref={menuRef}
                className={`
                    absolute z-[60] -translate-x-1/2 rounded-lg
                    shadow-lg transition-opacity duration-200
                    ${dropdownState.placement === 'top' ? '-translate-y-full' : ''}
                    ${dropdownState.active ? 'pointer-events-none opacity-100' : 'pointer-events-none opacity-0'}
                    ${!isLoggedIn && isTooltipVisible ? 'text-[12px]' : ''}
                  `}
                style={{
                  top: dropdownState.position.y,
                  left: dropdownState.position.x,
                }}
              >
                <Tooltip
                  label='リアクションを利用するには、ログインしてください。'
                  weight='500'
                />
              </div>
            </ReactionMenu>
          ) : null}
        </>
      )}
      {isMobile && showUserBottomMenu && (
        <ReactionUserBottomMenu
          handleCloseClick={() => setShowUserBottomMenu(false)}
          users={localReaction?.users}
          emoji={localReaction?.emoji}
        />
      )}
      {isMobile && showTenantMemberTooltip && (
        <div className='pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 -translate-y-1/3 whitespace-nowrap text-[12px]'>
          <Tooltip
            label='リアクション権限がありません。管理者にお問い合わせください。'
            weight='500'
          />
        </div>
      )}
      {isMobile && isTooltipVisible && (
        <div className='pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 -translate-y-1/3 whitespace-nowrap text-[12px]'>
          <Tooltip
            label='リアクションするには、ログインしてください。'
            weight='500'
          />
        </div>
      )}
    </div>
  );
};

export default ReactionItem;
